import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Title, Tabs, Button, Message, Spinner, Expander, useToast, Panel, Selectbox } from './bpm';
import {
  analyseAPI,
  settingsAPI,
  xFeedAPI,
  twsAPI,
  portfolioAPI,
  categoriesAPI,
  assetEnrichmentAPI,
} from '../services/api';
import {
  totalApportsEur,
  buildEnrichmentMapFromBulk,
  computePortfolioDistributionByCategory,
  buildPortfolioDetailMarkdownTable,
} from '../utils/beamExportPortfolio';
import { usePortfolio } from '../contexts/PortfolioContextIndex';
import { useAuth } from '../contexts/AuthContext';
import { useCanEdit } from '../hooks/useCanEdit';
import { getPageIcon } from '../utils/pageIcons';
import { usePageRenderTime, logDataLoad } from '../utils/performanceLogger';
import './Analyse.css';

const DEFAULT_VEILLE_SOURCES_JSON = `[
  { "handle": "@Compte_Tier1", "tier": 1 },
  { "handle": "@Compte_Tier2", "tier": 2 },
  { "handle": "@Compte_Tier3", "tier": 3 }
]`;

/** Toast export : pas de JSON brut ni de détail technique pour l’utilisateur. */
async function getBeamExportErrorToastMessage(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  let detailText = '';
  let isValidation = false;

  const readDetailFromObject = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    const d = obj.detail;
    if (typeof d === 'string') {
      detailText = d.trim();
    } else if (Array.isArray(d)) {
      isValidation = true;
    }
  };

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const trimmed = text.trim();
      if (trimmed.startsWith('{')) {
        try {
          readDetailFromObject(JSON.parse(trimmed));
        } catch {
          isValidation = true;
        }
      } else if (trimmed) {
        detailText = trimmed;
      }
    } catch {
      /* ignore */
    }
  } else if (data && typeof data === 'object') {
    readDetailFromObject(data);
  }

  if (!err.response) {
    return 'Réseau indisponible — réessaie.';
  }
  if (status === 401) {
    return 'Session expirée — reconnecte-toi.';
  }
  if (status === 400) {
    if (
      /Résumé compte|accountSummary|netLiquidation|Connectez IB Gateway|IB Gateway/i.test(detailText)
    ) {
      return 'IB Gateway non connecté — connecte-toi avant d\'exporter.';
    }
    if (detailText && detailText.length < 400 && !/^\s*\{/.test(detailText)) {
      return detailText;
    }
    return 'Données export invalides — rafraîchis le portefeuille.';
  }
  if (status === 422 || isValidation) {
    return 'Données export invalides — rafraîchis le portefeuille.';
  }
  if (status >= 500) {
    if (detailText && detailText.length < 400 && !/[{}[\]]/.test(detailText)) {
      return detailText;
    }
    return 'Erreur export — voir les logs pour le détail';
  }
  if (detailText && detailText.length < 400 && !/^\s*\{/.test(detailText)) {
    return detailText;
  }
  return 'Erreur export — voir les logs pour le détail';
}

/** Nom de fichier type 20260402_2122_BEAM_Export.md — fuseau Europe/Paris (heure légale française). */
function filenameBeamExportParis() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const pick = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const y = pick('year');
  const m = pick('month').padStart(2, '0');
  const d = pick('day').padStart(2, '0');
  const h = pick('hour').padStart(2, '0');
  const min = pick('minute').padStart(2, '0');
  return `${y}${m}${d}_${h}${min}_BEAM_Export.md`;
}

const Analyse = ({ onLogoClick = null }) => {
  usePageRenderTime('Analyse');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyses, setAnalyses] = useState([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(false);
  const [metaAnalysis, setMetaAnalysis] = useState(null);
  const [loadingMetaAnalysis, setLoadingMetaAnalysis] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { showToast } = useToast();
  const { positions, isConnected, account } = usePortfolio();
  const { profile } = useAuth();
  const { canEdit } = useCanEdit();
  
  // États pour les prompts
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [metaAnalysisPrompt, setMetaAnalysisPrompt] = useState('');
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [savingPrompts, setSavingPrompts] = useState(false);
  
  // État pour les comptes X suivis
  const [xAccounts, setXAccounts] = useState('');
  const [savingXAccounts, setSavingXAccounts] = useState(false);
  
  // États pour le feed X
  const [xTweets, setXTweets] = useState([]);
  const [loadingXTweets, setLoadingXTweets] = useState(false);
  const [loadingMoreTweets, setLoadingMoreTweets] = useState(false);
  const [hasNoMoreTweets, setHasNoMoreTweets] = useState(false);
  const [xFeedError, setXFeedError] = useState(null);
  const [xFeedDiagnostic, setXFeedDiagnostic] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const TWEETS_PAGE_SIZE = 20;
  /** Évite de recharger les tweets à chaque passage sur l'onglet Feed : chargement uniquement à la première visite (ou via Rafraîchir). */
  const feedLoadedRef = useRef(false);
  /** Horodatage du dernier chargement réussi du feed X (note export). */
  const xFeedLoadedAtRef = useRef(null);

  /** null = aucun export en cours ; sinon le seul bouton concerné affiche « Export en cours… » */
  const [exportLoadingMode, setExportLoadingMode] = useState(null);
  const [exportContextLoading, setExportContextLoading] = useState(false);
  const [sessionNotesExport, setSessionNotesExport] = useState('');
  const [positionMetaJson, setPositionMetaJson] = useState('{}');
  const [veilleSourcesJson, setVeilleSourcesJson] = useState(DEFAULT_VEILLE_SOURCES_JSON);
  const [manualFeedEntries, setManualFeedEntries] = useState([]);
  const [savingExportContext, setSavingExportContext] = useState(false);
  const [mfHandle, setMfHandle] = useState('');
  const [mfText, setMfText] = useState('');
  const [mfSignal, setMfSignal] = useState('informatif');
  const [mfCategory, setMfCategory] = useState('macro_structurel');
  const [mfTickers, setMfTickers] = useState('');
  const [mfTs, setMfTs] = useState('');
  
  // Prompts par défaut
  const defaultAnalysisPrompt = `Tu es un analyste financier expert. Analyse ce document PDF intitulé "{nom_fichier}" et génère une synthèse actionnable.

Date d'analyse: {date_actuelle}

Document à analyser:
{texte_limite}

Génère une analyse structurée en français avec:
1. **Date d'analyse**: {date_actuelle}
2. **Résumé exécutif** (2-3 phrases)
3. **Points clés** (3-5 points principaux)
4. **Recommandations actionnables** (actions concrètes à entreprendre)
5. **Risques identifiés** (si applicable)
6. **Opportunités** (si applicable)

Format: Texte structuré, professionnel, sans emoji, maximum 500 mots. La date d'analyse doit être clairement indiquée au début de l'analyse.`;

  const defaultMetaAnalysisPrompt = `Tu es un analyste financier expert. Analyse l'ensemble des {nombre_documents} documents analysés dans les 30 derniers jours et génère une méta-analyse synthétique.

Date de la méta-analyse: {date_actuelle}
Période analysée: du {date_debut} au {date_fin}
Nombre de documents: {nombre_documents}
{tickers_section}
Analyses individuelles agrégées:
{texte_agrege_limite}

Génère une méta-analyse structurée en français avec:
1. **Date de la méta-analyse**: {date_actuelle}
2. **Période analysée**: du {date_debut} au {date_fin}
3. **Synthèse globale** (3-5 phrases sur les tendances générales)
4. **Thèmes récurrents** (points communs identifiés dans plusieurs documents)
5. **Évolutions temporelles** (changements ou tendances observées sur la période)
6. **Recommandations stratégiques** (actions prioritaires basées sur l'ensemble des analyses)
7. **Risques agrégés** (risques communs ou émergents)
8. **Opportunités consolidées** (opportunités identifiées dans plusieurs documents)
{portfolio_tickers}

Format: Texte structuré, professionnel, sans emoji, maximum 800 mots. La date et la période doivent être clairement indiquées. Les tickers doivent être inclus en fin de document dans une section dédiée si disponibles.`;
  
  // Extraire les tickers uniques des positions
  const portfolioTickers = useMemo(() => {
    if (!positions || positions.length === 0) return [];
    const tickers = positions
      .map(p => p.symbol)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index) // Dédupliquer
      .sort();
    return tickers;
  }, [positions]);

  // Chargement différé par onglet (comme Compte) - ne charger que les données de l'onglet actif
  useEffect(() => {
    if (activeTab === 0) {
      // Onglet Analyse : méta-analyse uniquement
      loadPreviousAnalyses();
      loadMetaAnalysis();
    } else if (activeTab === 1) {
      // Onglet Rapports : liste des analyses
      loadPreviousAnalyses();
    } else if (activeTab === 2) {
      // Onglet Feed : ne charger qu'à la première visite (éviter une requête X à chaque passage)
      if (!feedLoadedRef.current) {
        feedLoadedRef.current = true;
        loadXTweets();
        loadDiagnostic();
      }
    } else if (activeTab === 4) {
      loadExportContext();
    } else if (activeTab === 5) {
      loadPrompts();
    }
  }, [activeTab]);

  // Charger le diagnostic
  const loadDiagnostic = useCallback(async () => {
    const t0 = performance.now();
    setLoadingDiagnostic(true);
    try {
      const response = await xFeedAPI.getDiagnostic();
      setXFeedDiagnostic(response.data);
    } catch (err) {
      console.error('❌ [X Feed] Erreur lors du chargement du diagnostic:', err);
      setXFeedDiagnostic(null);
    } finally {
      setLoadingDiagnostic(false);
      const duration = performance.now() - t0;
      logDataLoad('Analyse - Diagnostic', duration, 'X API');
    }
  }, []);
  
  // Charger le feed X (premier chargement : 20 tweets)
  const loadXTweets = useCallback(async () => {
    const t0 = performance.now();
    setLoadingXTweets(true);
    setXFeedError(null);
    setHasNoMoreTweets(false);
    try {
      const response = await xFeedAPI.getTweets(TWEETS_PAGE_SIZE);
      setXTweets(response.data?.tweets || []);
      xFeedLoadedAtRef.current = new Date().toISOString();
    } catch (err) {
      console.error('❌ [X Feed] Erreur lors du chargement des posts:', err);
      console.error('📊 [X Feed] Détails de l\'erreur:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      // Stocker le message d'erreur pour l'affichage
      let errorMessage = null;
      
      // Afficher un message d'erreur plus spécifique
      if (err.response?.status === 402) {
        const errorDetail = err.response?.data?.detail || 'Quota X API épuisé';
        errorMessage = errorDetail;
        showToast(errorDetail, 'warning', 3000, 'Avertissement API X', 'Analyse', getPageIcon('Analyse'));
        console.error('❌ [X Feed] Quota X API épuisé:', errorDetail);
      } else if (err.response?.status === 401) {
        const errorDetail = err.response?.data?.detail || 'Clé API X invalide ou expirée';
        errorMessage = errorDetail;
        showToast(errorDetail, 'error', 3000, 'Erreur API X', 'Analyse', getPageIcon('Analyse'));
        console.error('❌ [X Feed] Clé API X invalide:', errorDetail);
      } else if (err.response?.status === 403) {
        const errorDetail = err.response?.data?.detail || 'Accès refusé par l\'API X';
        errorMessage = errorDetail;
        showToast(errorDetail, 'error', 3000, 'Erreur API X', 'Analyse', getPageIcon('Analyse'));
        console.error('❌ [X Feed] Accès refusé:', errorDetail);
      } else if (err.response?.status === 500) {
        const errorDetail = err.response?.data?.detail || 'Erreur serveur';
        if (errorDetail.includes('non configurée')) {
          errorMessage = 'Clé API X non configurée. Veuillez la configurer dans Paramètres > API.';
          showToast('Veuillez configurer la clé API X dans Paramètres > API pour accéder au feed X.', 'warning', 3000, 'Clé API X manquante', 'Analyse', getPageIcon('Analyse'));
        } else {
          errorMessage = `Erreur serveur: ${errorDetail}`;
          console.error('❌ [X Feed] Erreur serveur:', errorDetail);
          showToast(errorDetail, 'error', 3000, 'Erreur serveur', 'Analyse', getPageIcon('Analyse'));
        }
      } else {
        errorMessage = 'Erreur lors de la récupération des tweets. Vérifiez votre connexion et réessayez.';
      }
      
      setXFeedError(errorMessage);
      setXTweets([]);
    } finally {
      setLoadingXTweets(false);
      const duration = performance.now() - t0;
      logDataLoad('Analyse - Feed', duration, 'tweets');
    }
  }, [showToast]);

  // Charger plus de tweets (bouton "Voir plus de tweets") — le bouton reste affiché tant qu'on reçoit de nouveaux tweets
  const loadMoreTweets = useCallback(async () => {
    if (loadingMoreTweets || hasNoMoreTweets || xTweets.length === 0) return;
    const currentCount = xTweets.length;
    const nextLimit = currentCount + TWEETS_PAGE_SIZE;
    setLoadingMoreTweets(true);
    setXFeedError(null);
    try {
      const response = await xFeedAPI.getTweets(nextLimit);
      const newTweets = response.data?.tweets || [];
      setXTweets(newTweets);
      xFeedLoadedAtRef.current = new Date().toISOString();
      // Plus de tweets dispo seulement si la réponse n'a pas ajouté de nouveaux (même nombre ou moins)
      if (newTweets.length <= currentCount) setHasNoMoreTweets(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Impossible de charger plus de tweets.';
      setXFeedError(msg);
      showToast(msg, 'error', 3000, 'Feed', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setLoadingMoreTweets(false);
    }
  }, [xTweets.length, loadingMoreTweets, hasNoMoreTweets, showToast]);
  
  // Charger les prompts depuis les paramètres
  const loadPrompts = useCallback(async () => {
    const t0 = performance.now();
    setLoadingPrompts(true);
    try {
      const organizationId = profile?.organization_id || 1;
      
      // Charger le prompt d'analyse unitaire
      try {
        const analysisPromptRes = await settingsAPI.get('analyse_prompt_template', organizationId);
        if (analysisPromptRes.data?.param_value) {
          setAnalysisPrompt(analysisPromptRes.data.param_value);
        } else {
          setAnalysisPrompt(defaultAnalysisPrompt);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Erreur lors du chargement du prompt d\'analyse:', err);
        }
        setAnalysisPrompt(defaultAnalysisPrompt);
      }
      
      // Charger le prompt de méta-analyse
      try {
        const metaPromptRes = await settingsAPI.get('meta_analyse_prompt_template', organizationId);
        if (metaPromptRes.data?.param_value) {
          setMetaAnalysisPrompt(metaPromptRes.data.param_value);
        } else {
          setMetaAnalysisPrompt(defaultMetaAnalysisPrompt);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Erreur lors du chargement du prompt de méta-analyse:', err);
        }
        setMetaAnalysisPrompt(defaultMetaAnalysisPrompt);
      }
      
      // Charger les comptes X suivis
      try {
        const xAccountsRes = await settingsAPI.get('x_followed_accounts', organizationId);
        if (xAccountsRes.data?.param_value) {
          setXAccounts(xAccountsRes.data.param_value);
        } else {
          setXAccounts('');
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Erreur lors du chargement des comptes X suivis:', err);
        }
        setXAccounts('');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des prompts:', err);
      setAnalysisPrompt(defaultAnalysisPrompt);
      setMetaAnalysisPrompt(defaultMetaAnalysisPrompt);
      setXAccounts('');
    } finally {
      setLoadingPrompts(false);
      const duration = performance.now() - t0;
      logDataLoad('Analyse - Prompts', duration, '3 paramètres');
    }
  }, [profile]);
  
  // Sauvegarder le prompt d'analyse unitaire
  const saveAnalysisPrompt = useCallback(async () => {
    setSavingPrompts(true);
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('analyse_prompt_template', analysisPrompt, organizationId);
      showToast('Le prompt d\'analyse unitaire a été sauvegardé avec succès.', 'success', 3000, 'Sauvegarde de prompt', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du prompt d\'analyse:', err);
      showToast('Une erreur est survenue lors de la sauvegarde du prompt d\'analyse.', 'error', 3000, 'Erreur de sauvegarde', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingPrompts(false);
    }
  }, [analysisPrompt, profile, showToast]);
  
  // Sauvegarder le prompt de méta-analyse
  const saveMetaAnalysisPrompt = useCallback(async () => {
    setSavingPrompts(true);
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('meta_analyse_prompt_template', metaAnalysisPrompt, organizationId);
      showToast('Le prompt de méta-analyse a été sauvegardé avec succès.', 'success', 3000, 'Sauvegarde de prompt', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du prompt de méta-analyse:', err);
      showToast('Une erreur est survenue lors de la sauvegarde du prompt de méta-analyse.', 'error', 3000, 'Erreur de sauvegarde', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingPrompts(false);
    }
  }, [metaAnalysisPrompt, profile, showToast]);
  
  // Restaurer le prompt d'analyse unitaire par défaut
  const restoreDefaultAnalysisPrompt = useCallback(() => {
    setAnalysisPrompt(defaultAnalysisPrompt);
    showToast('Le prompt d\'analyse unitaire par défaut a été restauré. Pensez à sauvegarder vos modifications.', 'info', 3000, 'Restauration de prompt', 'Analyse', getPageIcon('Analyse'));
  }, [showToast]);
  
  // Restaurer le prompt de méta-analyse par défaut
  const restoreDefaultMetaAnalysisPrompt = useCallback(() => {
    setMetaAnalysisPrompt(defaultMetaAnalysisPrompt);
    showToast('Le prompt de méta-analyse par défaut a été restauré. Pensez à sauvegarder vos modifications.', 'info', 3000, 'Restauration de prompt', 'Analyse', getPageIcon('Analyse'));
  }, [showToast]);
  
  // Sauvegarder les comptes X suivis
  const saveXAccounts = useCallback(async () => {
    setSavingXAccounts(true);
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('x_followed_accounts', xAccounts, organizationId);
      showToast('La liste des comptes X suivis a été sauvegardée avec succès.', 'success', 3000, 'Sauvegarde des comptes X', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des comptes X suivis:', err);
      showToast('Une erreur est survenue lors de la sauvegarde des comptes X suivis.', 'error', 3000, 'Erreur de sauvegarde', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingXAccounts(false);
    }
  }, [xAccounts, profile, showToast]);

  const loadExportContext = useCallback(async () => {
    const organizationId = profile?.organization_id || 1;
    setExportContextLoading(true);
    try {
      try {
        const sn = await settingsAPI.get('beam_export_session_notes', organizationId);
        setSessionNotesExport(sn.data?.param_value || '');
      } catch (e) {
        if (e.response?.status !== 404) console.error(e);
        setSessionNotesExport('');
      }
      try {
        const pm = await settingsAPI.get('beam_export_position_meta', organizationId);
        const pv = pm.data?.param_value;
        setPositionMetaJson(pv && String(pv).trim() ? pv : '{}');
      } catch (e) {
        if (e.response?.status !== 404) console.error(e);
        setPositionMetaJson('{}');
      }
      let veilleStr = DEFAULT_VEILLE_SOURCES_JSON;
      try {
        const vs = await settingsAPI.get('beam_veille_sources', organizationId);
        if (vs.data?.param_value && String(vs.data.param_value).trim()) {
          veilleStr = vs.data.param_value;
        }
      } catch (e) {
        if (e.response?.status !== 404) console.error(e);
      }
      setVeilleSourcesJson(veilleStr);
      try {
        const arr = JSON.parse(veilleStr);
        if (Array.isArray(arr) && arr[0]?.handle) {
          setMfHandle(String(arr[0].handle));
        }
      } catch {
        /* ignore */
      }
      try {
        const mf = await settingsAPI.get('beam_manual_feed_entries', organizationId);
        const raw = mf.data?.param_value;
        let arr = [];
        if (raw) {
          try {
            arr = JSON.parse(raw);
          } catch {
            arr = [];
          }
        }
        setManualFeedEntries(Array.isArray(arr) ? arr : []);
      } catch (e) {
        if (e.response?.status !== 404) console.error(e);
        setManualFeedEntries([]);
      }
      const d = new Date();
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      setMfTs(d.toISOString().slice(0, 16));
    } finally {
      setExportContextLoading(false);
    }
  }, [profile?.organization_id]);

  const saveSessionNotesExport = useCallback(async () => {
    setSavingExportContext(true);
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('beam_export_session_notes', sessionNotesExport, organizationId);
      showToast('Notes de session enregistrées.', 'success', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      showToast('Erreur lors de la sauvegarde des notes.', 'error', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingExportContext(false);
    }
  }, [sessionNotesExport, profile, showToast]);

  const savePositionMetaJson = useCallback(async () => {
    setSavingExportContext(true);
    try {
      JSON.parse(positionMetaJson);
    } catch {
      showToast('JSON métadonnées positions invalide.', 'warning', 4000, 'Export', 'Analyse', getPageIcon('Analyse'));
      setSavingExportContext(false);
      return;
    }
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('beam_export_position_meta', positionMetaJson, organizationId);
      showToast('Métadonnées positions enregistrées.', 'success', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      showToast('Erreur sauvegarde métadonnées.', 'error', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingExportContext(false);
    }
  }, [positionMetaJson, profile, showToast]);

  const saveVeilleSourcesJson = useCallback(async () => {
    setSavingExportContext(true);
    try {
      JSON.parse(veilleSourcesJson);
    } catch {
      showToast('JSON Sources veille invalide.', 'warning', 4000, 'Export', 'Analyse', getPageIcon('Analyse'));
      setSavingExportContext(false);
      return;
    }
    try {
      const organizationId = profile?.organization_id || 1;
      await settingsAPI.update('beam_veille_sources', veilleSourcesJson, organizationId);
      showToast('Sources veille enregistrées.', 'success', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      showToast('Erreur sauvegarde sources veille.', 'error', 3000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setSavingExportContext(false);
    }
  }, [veilleSourcesJson, profile, showToast]);

  const addManualFeedEntry = useCallback(async () => {
    if (!mfText.trim()) {
      showToast('Saisissez le texte du post.', 'warning', 3000, 'Feed manuel', 'Analyse', getPageIcon('Analyse'));
      return;
    }
    const organizationId = profile?.organization_id || 1;
    const iso = mfTs ? new Date(mfTs).toISOString() : new Date().toISOString();
    const entry = {
      handle: mfHandle || 'N/D',
      text: mfText.trim(),
      signal: mfSignal,
      category: mfCategory,
      tickers: mfTickers.trim(),
      timestamp: iso,
    };
    const prev = manualFeedEntries;
    const next = [entry, ...prev];
    setManualFeedEntries(next);
    try {
      await settingsAPI.update('beam_manual_feed_entries', JSON.stringify(next), organizationId);
      setMfText('');
      showToast('Entrée ajoutée au feed manuel.', 'success', 3000, 'Feed manuel', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      showToast('Erreur sauvegarde du feed manuel.', 'error', 3000, 'Feed manuel', 'Analyse', getPageIcon('Analyse'));
      setManualFeedEntries(prev);
    }
  }, [mfHandle, mfText, mfSignal, mfCategory, mfTickers, mfTs, manualFeedEntries, profile, showToast]);

  const handleExportMarkdown = useCallback(async (windowMode) => {
    if (!isConnected) {
      showToast(
        'IB Gateway non connecté — connecte-toi avant d\'exporter',
        'error',
        5000,
        'Export',
        'Analyse',
        getPageIcon('Analyse')
      );
      return;
    }
    setExportLoadingMode(windowMode);
    try {
      let asRes;
      try {
        asRes = await twsAPI.getAccountSummary();
      } catch (accErr) {
        console.error('[Export BEAM] account-summary', accErr);
        const msg = await getBeamExportErrorToastMessage(accErr);
        showToast(
          /Gateway|connect|non connecté|Session/i.test(msg)
            ? 'IB Gateway non connecté — connecte-toi avant d\'exporter'
            : msg,
          'error',
          5000,
          'Export',
          'Analyse',
          getPageIcon('Analyse')
        );
        return;
      }
      let posList = Array.isArray(positions) ? positions : [];
      try {
        const posRes = await twsAPI.getPositions();
        if (Array.isArray(posRes.data)) {
          posList = posRes.data;
        }
      } catch (posErr) {
        console.warn('[Export BEAM] getPositions échoue — repli sur le contexte portefeuille puis cache Redis', posErr);
        if (!Array.isArray(posList)) posList = [];
        if (posList.length === 0) {
          try {
            const cacheRes = await portfolioAPI.getCachedPositions();
            const cached = cacheRes.data?.positions;
            if (Array.isArray(cached)) {
              posList = cached;
            }
          } catch (cacheErr) {
            console.warn('[Export BEAM] cached-positions indisponible', cacheErr);
          }
        }
      }

      const [apportsRes, categoriesList] = await Promise.all([
        portfolioAPI.getApports().catch(() => ({ data: [] })),
        (async () => {
          try {
            const saved = sessionStorage.getItem('portefeuille_categories');
            const ts = sessionStorage.getItem('portefeuille_categories_timestamp');
            if (saved && ts && Date.now() - parseInt(ts, 10) < 5 * 60 * 1000) {
              const cats = JSON.parse(saved);
              return Array.isArray(cats) ? cats.filter((c) => c.is_active) : [];
            }
          } catch (_) {
            /* ignore */
          }
          try {
            const r = await categoriesAPI.list();
            const data = r.data || [];
            try {
              sessionStorage.setItem('portefeuille_categories', JSON.stringify(data));
              sessionStorage.setItem('portefeuille_categories_timestamp', Date.now().toString());
            } catch (_) {
              /* ignore */
            }
            return data.filter((c) => c.is_active);
          } catch (_) {
            return [];
          }
        })(),
      ]);

      const refCap = totalApportsEur(apportsRes.data);
      const symList = [...new Set(posList.map((p) => p.symbol).filter(Boolean))];
      let enrichmentMap = new Map();
      if (symList.length > 0) {
        try {
          const bulk = await assetEnrichmentAPI.getBulkBySymbols(symList);
          enrichmentMap = buildEnrichmentMapFromBulk(bulk.data);
        } catch (enrErr) {
          console.warn('[Export BEAM] enrichissement symboles', enrErr);
        }
      }
      const { byCategory: portfolioExpositionByCategory } = computePortfolioDistributionByCategory(
        posList,
        categoriesList,
        enrichmentMap,
        account
      );

      let tweetsForExport = xTweets;
      let feedStaleNote = null;
      try {
        const tr = await xFeedAPI.getTweets(50);
        const fresh = tr.data?.tweets || [];
        if (fresh.length > 0) {
          tweetsForExport = fresh;
        }
      } catch (feedErr) {
        console.warn('[Export BEAM] flux X (repli cache onglet Feed)', feedErr);
        if (tweetsForExport.length === 0) {
          feedStaleNote =
            'Impossible de charger le feed X ; aucun post en cache. Ouvre l’onglet Feed ou réessaie.';
        }
      }
      if (tweetsForExport.length === 0 && !feedStaleNote) {
        feedStaleNote = 'Aucun post X disponible pour cet export.';
      }

      const res = await analyseAPI.exportMarkdown({
        window: windowMode,
        accountSummary: asRes.data,
        positions: posList,
        fxRates: {},
        feedAutoTweets: tweetsForExport,
        feedStaleNote,
        feedCacheFetchedAt: xFeedLoadedAtRef.current,
        ...(refCap != null && refCap > 0 ? { referenceCapitalEur: refCap } : {}),
        ...(posList.length > 0
          ? {
              portfolioExpositionByCategory,
              portfolioDetailMarkdown: buildPortfolioDetailMarkdownTable(posList, enrichmentMap),
            }
          : {}),
      });
      const blob = res.data;
      if (!(blob instanceof Blob) || (blob.type && blob.type.includes('json'))) {
        console.error('[Export BEAM] réponse inattendue (pas un fichier MD)', blob);
        showToast(
          'Erreur export — voir les logs pour le détail',
          'error',
          5000,
          'Export',
          'Analyse',
          getPageIcon('Analyse')
        );
        return;
      }
      let fname = filenameBeamExportParis();
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";\n]+)"?/i);
        if (m) fname = m[1].trim();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      try {
        a.click();
      } catch (dlErr) {
        console.error('[Export BEAM] téléchargement', dlErr);
        showToast(
          'Impossible d\'écrire le fichier — vérifie les permissions du dossier',
          'error',
          5000,
          'Export',
          'Analyse',
          getPageIcon('Analyse')
        );
        URL.revokeObjectURL(url);
        a.remove();
        return;
      }
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`Export généré : ${fname}`, 'success', 4000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } catch (err) {
      console.error('[Export BEAM]', err, err.response?.status, err.response?.data);
      const msg = await getBeamExportErrorToastMessage(err);
      showToast(msg, 'error', 5000, 'Export', 'Analyse', getPageIcon('Analyse'));
    } finally {
      setExportLoadingMode(null);
    }
  }, [showToast, isConnected, xTweets, positions, account]);

  const veilleHandleOptions = useMemo(() => {
    try {
      const a = JSON.parse(veilleSourcesJson || '[]');
      if (!Array.isArray(a)) return [];
      return a.map((x, i) => ({
        value: x.handle || `__h${i}`,
        label: `${x.handle || '?'} (priorité ${x.tier ?? '?'})`,
      }));
    } catch {
      return [];
    }
  }, [veilleSourcesJson]);

  const loadPreviousAnalyses = useCallback(async () => {
    const t0 = performance.now();
    setLoadingAnalyses(true);
    try {
      const response = await analyseAPI.list(10);
      setAnalyses(response.data?.analyses || response.data || []);
    } catch (err) {
      console.error('❌ [Analyse] Erreur lors du chargement des analyses:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      console.error('📊 [Analyse] Détails de l\'erreur:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      
      // Message d'erreur plus spécifique selon le type d'erreur
      let userMessage = errorMessage;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        userMessage = 'Le serveur ne répond pas dans les temps. Vérifiez que le backend est démarré sur http://localhost:8000';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        userMessage = 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré.';
      }
      
      showToast(userMessage, 'error', 3000, 'Erreur de chargement', 'Analyse', getPageIcon('Analyse'));
    } finally {
      const duration = performance.now() - t0;
      setLoadingAnalyses(false);
      logDataLoad('Analyse - Liste', duration, 'analyses précédentes');
    }
  }, [showToast]);

  const loadMetaAnalysis = useCallback(async (forceRegenerate = false) => {
    const t0 = performance.now();
    setLoadingMetaAnalysis(true);
    try {
      const response = await analyseAPI.getMetaAnalysis(forceRegenerate);
      setMetaAnalysis(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setMetaAnalysis(null);
        return;
      }
      console.error('❌ [Analyse] Erreur lors du chargement de la méta-analyse:', err);
      // Ne pas afficher de toast pour les erreurs de méta-analyse (c'est optionnel)
      setMetaAnalysis(null);
    } finally {
      const duration = performance.now() - t0;
      setLoadingMetaAnalysis(false);
      logDataLoad('Analyse - Méta', duration, 'méta-analyse 30 jours');
    }
  }, []);

  const processFiles = useCallback((files) => {
    const fileArray = Array.isArray(files) ? files : Array.from(files);
    
    // Filtrer uniquement les PDF
    const pdfFiles = fileArray.filter(file => file.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length !== fileArray.length) {
      showToast('Seuls les fichiers PDF sont acceptés. Veuillez sélectionner des fichiers au format PDF.', 'warning', 3000, 'Format de fichier invalide', 'Analyse', getPageIcon('Analyse'));
    }
    
    // Limiter à 10 fichiers
    const limitedFiles = pdfFiles.slice(0, 10);
    
    if (pdfFiles.length > 10) {
      showToast('Maximum 10 fichiers autorisés. Seuls les 10 premiers fichiers seront traités.', 'warning', 3000, 'Limite de fichiers', 'Analyse', getPageIcon('Analyse'));
    }
    
    setSelectedFiles(limitedFiles);
  }, [showToast]);

  const handleFileSelect = (e) => {
    processFiles(e.target.files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ne désactiver le drag que si on quitte vraiment la zone (pas juste un enfant)
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showToast('Veuillez sélectionner au moins un fichier PDF à analyser.', 'warning', 3000, 'Aucun fichier sélectionné', 'Analyse', getPageIcon('Analyse'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let response;
      
      if (selectedFiles.length === 1) {
        // Upload unique
        response = await analyseAPI.uploadSingle(selectedFiles[0]);
        
        if (response.data) {
            showToast('Le document a été analysé avec succès. Les résultats sont disponibles.', 'success', 3000, 'Analyse de document', 'Analyse', getPageIcon('Analyse'));
          // Ajouter à la liste des analyses
          setAnalyses(prev => [response.data, ...prev]);
          // Déclencher la méta-analyse après l'analyse (forcer la régénération)
          await loadMetaAnalysis(true);
        }
      } else {
        // Upload multiple
        response = await analyseAPI.uploadMultiple(selectedFiles);
        
        if (response.data) {
          const results = Array.isArray(response.data) ? response.data : response.data.analyses || [];
          const errors = response.data.errors || [];
          
          if (results.length > 0) {
            showToast(`${results.length} document(s) analysé(s) avec succès. Les résultats sont disponibles dans la liste.`, 'success', 3000, 'Analyse de documents', 'Analyse', getPageIcon('Analyse'));
            // Ajouter à la liste des analyses
            setAnalyses(prev => [...results, ...prev]);
            // Déclencher la méta-analyse après les analyses (forcer la régénération)
            await loadMetaAnalysis(true);
          }
          
          if (errors.length > 0) {
            showToast(`${errors.length} erreur(s) rencontrée(s) lors de l'analyse. Certains documents n'ont pas pu être traités.`, 'warning', 3000, 'Analyse de documents', 'Analyse', getPageIcon('Analyse'));
            console.error('Erreurs:', errors);
          }
        }
      }
      
      // Réinitialiser la sélection
      setSelectedFiles([]);
      // Réinitialiser l'input file
      const fileInput = document.getElementById('pdf-upload-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (err) {
      console.error('Erreur lors de l\'upload:', err);
      
      // Récupérer le message d'erreur détaillé depuis le backend
      let errorMsg = 'Erreur lors de l\'analyse';
      
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      // Afficher le message d'erreur
      showToast(errorMsg, 'error', 3000, 'Erreur d\'analyse', 'Analyse', getPageIcon('Analyse'));
      
      // Logger les détails pour le débogage
      console.error('📊 [Analyse] Détails de l\'erreur:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const canAccessAnalyseTab = useCallback((tabId) => {
    if (!profile) return true;
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    return allowed.includes(tabId);
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner]);

  const allTabs = [
    {
      tabId: 'analyse-analyse',
      label: 'Analyse',
      content: (
        <div className="analyse-tab-content">
          {/* Méta-analyse : hors Expander, mise en page markdown (##, ***, ---, etc.) */}
          <div style={{ marginBottom: '2rem' }} data-name="meta_analyse">
            <Title level={2} style={{ marginBottom: '1rem' }}>
              Méta-analyse sur 30 jours glissants
            </Title>
            {loadingMetaAnalysis ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 0' }}>
                <Spinner text="Chargement de la méta-analyse..." size="medium" />
              </div>
            ) : metaAnalysis?.analyse_ia ? (
              <div className="analyse-content analyse-meta-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {metaAnalysis.analyse_ia}
                </ReactMarkdown>
                {portfolioTickers.length > 0 && (
                  <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
                    <h4 className="analyse-heading" style={{ marginBottom: '0.75rem' }}>
                      Tickers des sociétés dans lesquelles MSA est investie
                    </h4>
                    <p className="analyse-paragraph" style={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: 'rgb(29, 29, 31)',
                      lineHeight: '1.6'
                    }}>
                      {portfolioTickers.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            ) : metaAnalysis ? (
              <Message type="warning">
                Aucune méta-analyse disponible.
              </Message>
            ) : null}
          </div>
        </div>
      )
    },
    {
      tabId: 'analyse-rapports',
      label: 'Rapports',
      content: (
        <div className="analyse-tab-content" data-name="rapports">
          <Title level={2} style={{ marginBottom: '1.5rem' }}>Analyses</Title>
          {loadingAnalyses ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0' }}>
              <Spinner text="Chargement des analyses..." size="medium" />
            </div>
          ) : analyses.length === 0 ? (
            <Message type="info" style={{ marginTop: '1.5rem' }}>
              Aucune analyse précédente. Importez un document PDF (onglet Import) pour commencer.
            </Message>
          ) : (
            <div className="analyse-list" style={{ marginTop: '1.5rem' }}>
              {analyses.map((analysis) => (
                <Expander
                  key={analysis.id}
                  title={`📄 ${analysis.nom_fichier} - ${formatDate(analysis.date_import)}`}
                  defaultExpanded={false}
                >
                  <div className="analyse-content">
                    {analysis.analyse_ia ? (
                      <div className="analyse-text analyse-meta-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {analysis.analyse_ia}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <Message type="warning">
                        Aucune analyse disponible pour ce document.
                      </Message>
                    )}
                  </div>
                </Expander>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      tabId: 'analyse-feed-x',
      label: 'Feed',
      content: (
        <div className="analyse-tab-content">
          <div className="analyse-feed-x-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <Title level={2} style={{ margin: 0 }}>Derniers posts</Title>
            <Button
              variant="secondary"
              size="small"
              disabled={loadingXTweets}
              onClick={() => { loadXTweets(); loadDiagnostic(); }}
            >
              {loadingXTweets ? 'Chargement…' : 'Rafraîchir le feed'}
            </Button>
          </div>
          
          {loadingXTweets ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0' }}>
              <Spinner text="Chargement des posts..." size="medium" />
            </div>
          ) : xTweets.length === 0 ? (
            <Message type={xFeedError ? "warning" : "info"} style={{ marginTop: '1.5rem' }}>
              {xFeedError ? (
                <>
                  <strong>Erreur :</strong> {xFeedError}
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Aucun post disponible.</strong>
                  </div>
                  
                  {xFeedDiagnostic && (
                    <Panel
                      variant={xFeedDiagnostic.issues?.length ? 'warning' : 'info'}
                      title="Diagnostic de configuration"
                      className="analyse-feed-diagnostic-box"
                    >
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                        <li style={{ marginBottom: '0.5rem' }}>
                          {xFeedDiagnostic.api_key_configured ? (
                            <span style={{ color: 'green' }}>✅ Clé API X configurée</span>
                          ) : (
                            <span style={{ color: 'red' }}>❌ Clé API X non configurée → <a href="#parametres" onClick={(e) => { e.preventDefault(); setTimeout(() => setActiveTab(5), 0); }} style={{ color: '#1DA1F2', textDecoration: 'underline', cursor: 'pointer' }}>Configurer dans Paramètres &gt; API</a></span>
                          )}
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          {xFeedDiagnostic.api_key_valid ? (
                            <span style={{ color: 'green' }}>✅ Clé API X valide</span>
                          ) : xFeedDiagnostic.api_key_configured ? (
                            <span style={{ color: 'orange' }}>⚠️ Clé API X rejetée par X (401) ou erreur de déchiffrement</span>
                          ) : (
                            <span style={{ color: 'gray' }}>—</span>
                          )}
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          {xFeedDiagnostic.accounts_configured ? (
                            <span style={{ color: 'green' }}>✅ {xFeedDiagnostic.accounts_count} compte(s) X configuré(s)</span>
                          ) : (
                            <span style={{ color: 'red' }}>❌ Aucun compte X configuré → <a href="#parametres" onClick={(e) => { e.preventDefault(); setTimeout(() => setActiveTab(5), 0); }} style={{ color: '#1DA1F2', textDecoration: 'underline', cursor: 'pointer' }}>Configurer dans Analyse &gt; Paramètres</a></span>
                          )}
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          {xFeedDiagnostic.api_connection_ok ? (
                            <span style={{ color: 'green' }}>✅ Connexion à l'API X OK</span>
                          ) : xFeedDiagnostic.api_key_valid && xFeedDiagnostic.accounts_configured ? (
                            <span style={{ color: 'orange' }}>⚠️ Problème de connexion à l'API X</span>
                          ) : (
                            <span style={{ color: 'gray' }}>—</span>
                          )}
                        </li>
                        <li>
                          {xFeedDiagnostic.api_quota_ok ? (
                            <span style={{ color: 'green' }}>✅ Quota X API disponible</span>
                          ) : xFeedDiagnostic.api_connection_ok ? (
                            <span style={{ color: 'red' }}>❌ Quota X API épuisé → Rechargez les crédits de votre compte X API</span>
                          ) : (
                            <span style={{ color: 'gray' }}>—</span>
                          )}
                        </li>
                      </ul>
                      {xFeedDiagnostic.issues && xFeedDiagnostic.issues.length > 0 && (
                        <div className="analyse-feed-diagnostic-issues">
                          <strong className="analyse-feed-diagnostic-issues-title">Problèmes détectés :</strong>
                          <ul className="analyse-feed-diagnostic-list">
                            {xFeedDiagnostic.issues.map((issue, index) => (
                              <li key={index} className="analyse-feed-diagnostic-issue">{issue}</li>
                            ))}
                          </ul>
                          {xFeedDiagnostic.details?.api_error_message && (
                            <p className="analyse-feed-diagnostic-api-message">
                              Réponse de l'API X : {xFeedDiagnostic.details.api_error_message}
                            </p>
                          )}
                          {xFeedDiagnostic.recommendations && xFeedDiagnostic.recommendations.length > 0 && (
                            <div className="analyse-feed-diagnostic-recs">
                              <strong className="analyse-feed-diagnostic-recs-title">Recommandations :</strong>
                              <ul className="analyse-feed-diagnostic-list analyse-feed-diagnostic-recs-list">
                                {xFeedDiagnostic.recommendations.map((rec, index) => (
                                  <li key={index}>{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </Panel>
                  )}
                  
                  {!xFeedDiagnostic && !loadingDiagnostic && (
                    <div className="analyse-feed-diagnostic-fallback">
                      <em>Diagnostic non disponible. Vérifiez manuellement :</em>
                      <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                        <li>Les comptes X suivis sont configurés dans l'onglet Paramètres</li>
                        <li>La clé API X est configurée dans Paramètres &gt; API</li>
                        <li>Votre compte X API a des crédits disponibles (quota non épuisé)</li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Message>
          ) : (
            <div className="analyse-feed-tweet-list" style={{ marginTop: '1.5rem' }}>
              {xTweets.map((tweet) => (
                <div key={tweet.id} className="analyse-feed-tweet-card">
                  <div className="analyse-feed-tweet-header">
                    <div>
                      <strong className="analyse-feed-tweet-author">{tweet.author_username}</strong>
                      {tweet.created_at && (
                        <span className="analyse-feed-tweet-date">
                          {new Date(tweet.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                    {tweet.url && (
                      <a
                        href={tweet.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="analyse-feed-tweet-link"
                      >
                        Voir sur X →
                      </a>
                    )}
                  </div>
                  <p className="analyse-feed-tweet-text">{tweet.text}</p>
                </div>
              ))}
              {xTweets.length > 0 && !hasNoMoreTweets && (
                <div style={{ marginTop: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    className="analyse-feed-show-more"
                    onClick={loadMoreTweets}
                    disabled={loadingMoreTweets}
                  >
                    {loadingMoreTweets ? 'Chargement…' : 'Voir plus de tweets'}
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )
    },
    {
      tabId: 'analyse-import',
      label: 'Import',
      content: (
        <div className="analyse-tab-content">
          <Title level={2}>Analyse de documents</Title>
          <p className="analyse-description">
            Importez des documents PDF (analyses, études, rapports) pour générer automatiquement 
            une synthèse actionnable grâce à Claude. Les analyses sont stockées en base de données 
            et peuvent être réexploitées dans d'autres onglets.
          </p>

          {/* Zone d'upload */}
          <div className="analyse-upload-section">
            <div 
              className={`analyse-upload-area ${isDragging ? 'analyse-upload-area-dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                id="pdf-upload-input"
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="pdf-upload-input" className="analyse-upload-label">
                <div className="analyse-upload-content">
                  <span className="analyse-upload-icon">📄</span>
                  <span className="analyse-upload-text">
                    {isDragging 
                      ? 'Déposez les fichiers PDF ici'
                      : selectedFiles.length > 0
                        ? `${selectedFiles.length} fichier(s) sélectionné(s)`
                        : 'Glissez-déposez ou cliquez pour sélectionner des fichiers PDF (jusqu\'à 10 fichiers)'}
                  </span>
                </div>
              </label>
            </div>

            {selectedFiles.length > 0 && (
              <div className="analyse-files-list">
                <div className="analyse-files-header">
                  <span>Fichiers sélectionnés ({selectedFiles.length})</span>
                </div>
                <ul className="analyse-files-items">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="analyse-file-item">
                      <span className="analyse-file-name">{file.name}</span>
                      <span className="analyse-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canEdit && (
            <div className="analyse-upload-actions">
              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || uploading}
                variant="secondary"
                size="medium"
              >
                {uploading 
                  ? 'Analyse en cours...'
                  : `Analyser ${selectedFiles.length > 0 ? `${selectedFiles.length} document(s)` : 'les documents'}`
                }
              </Button>
              {uploading && (
                <Spinner size="small" style={{ marginLeft: '0.75rem' }} />
              )}
            </div>
            )}
          </div>
        </div>
      )
    },
    {
      tabId: 'analyse-export',
      label: 'Export',
      content: (
        <div className="analyse-tab-content">
          <Title level={2}>Export pour Claude</Title>
          <p className="analyse-description">
            Génère un fichier Markdown autonome (positions IBKR, options, alertes R1–R4, feed X manuel)
            à coller dans Claude pour analyse. IB Gateway doit être connecté.
          </p>
          <div className="analyse-upload-actions" style={{ marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <Button
              type="button"
              onClick={() => handleExportMarkdown('daily')}
              disabled={exportLoadingMode !== null}
              variant="secondary"
              size="medium"
            >
              {exportLoadingMode === 'daily' ? 'Export en cours…' : 'Export analyse journalière'}
            </Button>
            <Button
              type="button"
              onClick={() => handleExportMarkdown('hebdo')}
              disabled={exportLoadingMode !== null}
              variant="secondary"
              size="medium"
            >
              {exportLoadingMode === 'hebdo' ? 'Export en cours…' : 'Export analyse hebdomadaire'}
            </Button>
          </div>

          {exportContextLoading ? (
            <Spinner text="Chargement du contexte export…" size="small" />
          ) : null}

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2} style={{ marginBottom: '1rem' }}>Notes de session</Title>
            <textarea
              value={sessionNotesExport}
              onChange={(e) => setSessionNotesExport(e.target.value)}
              rows={4}
              placeholder="Observations, contexte, questions pour Claude…"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.875rem',
                border: '1px solid rgb(237, 237, 237)',
                borderRadius: '8px',
                resize: 'vertical',
                marginBottom: '0.75rem',
              }}
            />
            {canEdit && (
              <Button onClick={saveSessionNotesExport} disabled={savingExportContext} variant="secondary" size="medium">
                Enregistrer les notes
              </Button>
            )}
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2} style={{ marginBottom: '1rem' }}>Métadonnées positions (JSON)</Title>
            <p className="analyse-description" style={{ marginBottom: '0.75rem' }}>
              Par ticker : <code>categorie</code> (miniere_or, miniere_argent, energie, asie, monetaire, physique, etf, junior_miner…),{' '}
              <code>conviction</code> (long_cycle, tactique), <code>r6_doc</code> (true/false), <code>junior_miner</code> (bool).
            </p>
            <textarea
              value={positionMetaJson}
              onChange={(e) => setPositionMetaJson(e.target.value)}
              rows={10}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                border: '1px solid rgb(237, 237, 237)',
                borderRadius: '8px',
                marginBottom: '0.75rem',
              }}
            />
            {canEdit && (
              <Button onClick={savePositionMetaJson} disabled={savingExportContext} variant="secondary" size="medium">
                Enregistrer les métadonnées
              </Button>
            )}
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2} style={{ marginBottom: '1rem' }}>Sources veille (JSON)</Title>
            <p className="analyse-description" style={{ marginBottom: '0.75rem' }}>
              Liste <code>{'[{ "handle": "@...", "tier": 1 }]'}</code> — la priorité export dépend du tier (1 = priorité 1).
            </p>
            <textarea
              value={veilleSourcesJson}
              onChange={(e) => setVeilleSourcesJson(e.target.value)}
              rows={8}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                border: '1px solid rgb(237, 237, 237)',
                borderRadius: '8px',
                marginBottom: '0.75rem',
              }}
            />
            {canEdit && (
              <Button onClick={saveVeilleSourcesJson} disabled={savingExportContext} variant="secondary" size="medium">
                Enregistrer les sources veille
              </Button>
            )}
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2} style={{ marginBottom: '1rem' }}>Feed X — saisie manuelle</Title>
            <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '36rem' }}>
              <Selectbox
                label="Compte (Sources veille)"
                options={veilleHandleOptions}
                value={mfHandle}
                onChange={setMfHandle}
                placeholder="Choisir un compte…"
              />
              <Selectbox
                label="Catégorie"
                options={[
                  { value: 'macro_structurel', label: 'macro_structurel' },
                  { value: 'technique_cycle', label: 'technique_cycle' },
                  { value: 'fondamental_miners', label: 'fondamental_miners' },
                  { value: 'geopolitique', label: 'geopolitique' },
                ]}
                value={mfCategory}
                onChange={setMfCategory}
              />
              <Selectbox
                label="Signal"
                options={[
                  { value: 'haussier', label: 'haussier' },
                  { value: 'baissier', label: 'baissier' },
                  { value: 'neutre', label: 'neutre' },
                  { value: 'alerte', label: 'alerte' },
                  { value: 'informatif', label: 'informatif' },
                ]}
                value={mfSignal}
                onChange={setMfSignal}
              />
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>Tickers (virgules)</label>
              <input
                type="text"
                value={mfTickers}
                onChange={(e) => setMfTickers(e.target.value)}
                placeholder="AAPL, GOLD, …"
                style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid rgb(237, 237, 237)' }}
              />
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>Horodatage</label>
              <input
                type="datetime-local"
                value={mfTs}
                onChange={(e) => setMfTs(e.target.value)}
                style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid rgb(237, 237, 237)' }}
              />
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>Texte du post</label>
              <textarea
                value={mfText}
                onChange={(e) => setMfText(e.target.value)}
                rows={4}
                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid rgb(237, 237, 237)', width: '100%' }}
              />
              {canEdit && (
                <Button onClick={addManualFeedEntry} variant="secondary" size="medium">
                  Ajouter au feed manuel
                </Button>
              )}
            </div>
            {manualFeedEntries.length > 0 && (
              <ul style={{ marginTop: '1rem', fontSize: '0.8125rem', paddingLeft: '1.25rem' }}>
                {manualFeedEntries.slice(0, 15).map((e, i) => (
                  <li key={i} style={{ marginBottom: '0.35rem' }}>
                    <strong>{e.handle}</strong> · {e.timestamp?.slice(0, 16) || ''} — {String(e.text || '').slice(0, 80)}
                    {String(e.text || '').length > 80 ? '…' : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )
    },
    {
      tabId: 'analyse-parametres',
      label: 'Paramètres',
      content: (
        <div className="analyse-tab-content">
          <Title level={2}>Paramètres des prompts</Title>
          
          <div style={{ 
            padding: '1rem', 
            backgroundColor: 'rgb(245, 245, 247)', 
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgb(29, 29, 31)',
            marginBottom: '2rem'
          }}>
            <strong>Note importante :</strong>
            <p style={{ marginTop: '0.5rem', marginBottom: '0' }}>
              Personnalisez les prompts utilisés pour l'analyse unitaire et la méta-analyse. Les variables disponibles sont : <code>{'{nom_fichier}'}</code>, <code>{'{date_actuelle}'}</code>, <code>{'{texte_limite}'}</code> pour l'analyse unitaire, et <code>{'{date_actuelle}'}</code>, <code>{'{date_debut}'}</code>, <code>{'{date_fin}'}</code>, <code>{'{nombre_documents}'}</code>, <code>{'{tickers_section}'}</code>, <code>{'{texte_agrege_limite}'}</code>, <code>{'{portfolio_tickers}'}</code> pour la méta-analyse.
            </p>
          </div>
          
          {loadingPrompts ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0' }}>
              <Spinner text="Chargement des prompts..." size="small" />
            </div>
          ) : (
            <>
              {/* Prompt d'analyse unitaire */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  color: 'rgb(29, 29, 31)'
                }}>
                  Prompt d'analyse unitaire
                </label>
                <textarea
                  value={analysisPrompt}
                  onChange={(e) => setAnalysisPrompt(e.target.value)}
                  rows={15}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    border: '1px solid rgb(237, 237, 237)',
                    borderRadius: '8px',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    marginBottom: '0.75rem'
                  }}
                />
                {/* Boutons pour le prompt d'analyse unitaire */}
                {canEdit && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button
                    onClick={saveAnalysisPrompt}
                    disabled={savingPrompts}
                    variant="secondary"
                    size="medium"
                  >
                    {savingPrompts ? 'Sauvegarde...' : 'Sauvegarder le prompt'}
                  </Button>
                  <Button
                    onClick={restoreDefaultAnalysisPrompt}
                    disabled={savingPrompts || loadingPrompts}
                    variant="secondary"
                    size="medium"
                  >
                    Prompt par défaut
                  </Button>
                </div>
                )}
              </div>
              
              {/* Prompt de méta-analyse */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  color: 'rgb(29, 29, 31)'
                }}>
                  Prompt de méta-analyse
                </label>
                <textarea
                  value={metaAnalysisPrompt}
                  onChange={(e) => setMetaAnalysisPrompt(e.target.value)}
                  rows={20}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    border: '1px solid rgb(237, 237, 237)',
                    borderRadius: '8px',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    marginBottom: '0.75rem'
                  }}
                />
                {/* Boutons pour le prompt de méta-analyse */}
                {canEdit && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button
                    onClick={saveMetaAnalysisPrompt}
                    disabled={savingPrompts}
                    variant="secondary"
                    size="medium"
                  >
                    {savingPrompts ? 'Sauvegarde...' : 'Sauvegarder le prompt'}
                  </Button>
                  <Button
                    onClick={restoreDefaultMetaAnalysisPrompt}
                    disabled={savingPrompts || loadingPrompts}
                    variant="secondary"
                    size="medium"
                  >
                    Prompt par défaut
                  </Button>
                </div>
                )}
              </div>
              
              {/* Autres paramètres */}
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
                <Title level={2} style={{ marginBottom: '1.5rem' }}>Autres paramètres</Title>
              
                {/* Comptes X suivis */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  color: 'rgb(29, 29, 31)'
                }}>
                  Comptes X (Twitter) suivis
                </label>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'rgb(107, 107, 107)',
                  marginBottom: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  Liste les comptes X (anciennement Twitter) que vous suivez pour l'analyse. Séparez les comptes par des virgules ou un par ligne (ex: @compte1, @compte2 ou @compte1\n@compte2).
                </p>
                <textarea
                  value={xAccounts}
                  onChange={(e) => setXAccounts(e.target.value)}
                  rows={8}
                  placeholder="@compte1, @compte2, @compte3"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    border: '1px solid rgb(237, 237, 237)',
                    borderRadius: '8px',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    marginBottom: '0.75rem'
                  }}
                />
                {/* Bouton pour sauvegarder les comptes X suivis */}
                {canEdit && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button
                    onClick={saveXAccounts}
                    disabled={savingXAccounts}
                    variant="secondary"
                    size="medium"
                  >
                    {savingXAccounts ? 'Sauvegarde...' : 'Sauvegarder les comptes'}
                  </Button>
                </div>
                )}
              </div>
              </div>
            </>
          )}
        </div>
      )
    }
  ];

  const tabs = allTabs.filter(tab => canAccessAnalyseTab(tab.tabId));

  useEffect(() => {
    if (activeTab >= tabs.length && tabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  return (
    <div className="analyse-page">
      <Tabs 
        tabs={tabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
};

export default Analyse;
